import React from "react";
import { Link } from "react-router-dom";
import { FacebookIcon, InstagramIcon, LinkedinIcon, MessageCircleIcon } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { company, images } from "../i18n/translations";
import { legal } from "../i18n/legal";
const legalLinks = [{
  key: "privacy",
  to: "/politica-de-privacidad"
}, {
  key: "terms",
  to: "/terminos-de-servicio"
}, {
  key: "deletion",
  to: "/eliminar-datos"
}] as const;
const quickLinks = [{
  id: "inicio",
  href: "#inicio"
}, {
  id: "nosotros",
  href: "#nosotros"
}, {
  id: "capacidad",
  href: "#capacidad"
}, {
  id: "galeria",
  href: "#galeria"
}, {
  id: "contacto",
  href: "#contacto"
}] as const;
export function Footer() {
  const {
    t,
    lang
  } = useLanguage();
  const labels = legal[lang].labels;
  return <footer className="w-full bg-black text-slate-300" data-id="element-18" data-mp="29y5l8cy0g70t">
      <div className="max-w-content mx-auto px-4 sm:px-6 py-16" data-id="element-19" data-mp="2eya0jzn6mvpg">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4" data-id="element-20" data-mp="2ezl541ogc7hm">
          {/* Brand */}
          <div data-id="element-21" data-mp="3la27gozaktsl">
            <img src="https://cdn.magicpatterns.com/uploads/nMs7chRckPuBx9PPhJVEqo/image.png" alt="Símbolo GRANSACA" className="h-12 w-auto" width={130} height={58} data-id="element-22" data-mp="3lbmvdvr3pehh" />
            <p className="mt-4 text-sm leading-relaxed text-slate-400 max-w-xs" data-id="element-23" data-mp="3q8di31xev8bv">{t.footer.tagline}</p>
            <div className="mt-5 flex items-center gap-3" data-id="element-24" data-mp="828qmgkzanef">
              {[FacebookIcon, InstagramIcon, LinkedinIcon, MessageCircleIcon].map((Icon, i) => <a key={i} href={i === 3 ? company.whatsappLink : "#"} target={i === 3 ? "_blank" : undefined} rel="noreferrer" aria-label="Social" className="flex h-9 w-9 items-center justify-center rounded-sm bg-white/5 text-slate-300 transition-colors hover:bg-orange hover:text-white" data-id="element-25" data-mp="322zr2mj94xe" data-mpi={i}>
                  <Icon size={18} data-id="element-26" data-mp="tvd0bu5d69g7" />
                </a>)}
            </div>
          </div>

          {/* Quick links */}
          <div data-id="element-27" data-mp="yuzd62ha5noh">
            <h3 className="font-heading font-bold uppercase text-white text-lg tracking-wide" data-id="element-28" data-mp="yupnyh427kju">
              {t.footer.quickLinks}
            </h3>
            <ul className="mt-4 space-y-2.5" data-id="element-29" data-mp="18jr2s3yvbvw8">
              {quickLinks.map(l => <li key={l.id} data-id="element-30" data-mp="1clmbhtj4pbk1">
                  <Link to={`/${l.href}`} className="text-sm text-slate-400 transition-colors hover:text-orange" data-id="element-31" data-mp="1ckknypkxewlf">
                    {t.nav[l.id]}
                  </Link>
                </li>)}
            </ul>

            <h3 className="mt-8 font-heading font-bold uppercase text-white text-lg tracking-wide" data-id="element-32" data-mp="1rjeoof17dnv2">
              {labels.legalTitle}
            </h3>
            <ul className="mt-4 space-y-2.5" data-id="element-33" data-mp="3oczspkkjszas">
              {legalLinks.map(l => <li key={l.key} data-id="element-34" data-mp="3tarq2u3ypdmd">
                  <Link to={l.to} className="text-sm text-slate-400 transition-colors hover:text-orange" data-id="element-35" data-mp="2af3qfjjjwrgg">
                    {labels[l.key]}
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Products */}
          <div data-id="element-36" data-mp="1wo8q4vn4bioh">
            <h3 className="font-heading font-bold uppercase text-white text-lg tracking-wide" data-id="element-37" data-mp="1worz3musiwqd">
              {t.footer.productsTitle}
            </h3>
            <ul className="mt-4 space-y-2.5" data-id="element-38" data-mp="2tbn4mn97p5jv">
              {t.products.items.slice(0, 6).map(p => <li key={p.name} data-id="element-39" data-mp="2kdb7019xh7hx">
                  <Link to="/#productos" className="text-sm text-slate-400 transition-colors hover:text-orange" data-id="element-40" data-mp="2kci4xdlwz664">
                    {p.name}
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Contact */}
          <div data-id="element-41" data-mp="13y9tof6lgj9s">
            <h3 className="font-heading font-bold uppercase text-white text-lg tracking-wide" data-id="element-42" data-mp="13xqeh68bw8zg">
              {t.footer.contactTitle}
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-400" data-id="element-43" data-mp="2wmzeu5h25zpv">
              <li className="font-medium text-slate-300" data-id="element-44" data-mp="2woaim5ai77gz">{company.legalName}</li>
              <li data-id="element-45" data-mp="2isyfpam72aoz">
                <a href={`tel:${company.phone}`} className="hover:text-orange transition-colors" data-id="element-46" data-mp="2nu5o9bw5vdwp">
                  {company.phone}
                </a>
              </li>
              <li data-id="element-47" data-mp="3bki376ptqaws">
                <a href={`mailto:${company.email}`} className="hover:text-orange transition-colors" data-id="element-48" data-mp="3bhvz5h256djb">
                  {company.email}
                </a>
              </li>
              <li data-id="element-49" data-mp="31l13d5x96r8q">{t.contact.address}</li>
              <li className="text-orange font-semibold pt-1" data-id="element-50" data-mp="36kdmbfl07e61">{company.website}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10" data-id="element-51" data-mp="1q5m41vhknnag">
        <div className="max-w-content mx-auto px-4 sm:px-6 py-6" data-id="element-52" data-mp="1v5gujxxfqauz">
          <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-id="element-53" data-mp="1v6rypyuslkmm">
            {legalLinks.map(l => <li key={l.key} data-id="element-54" data-mp="1nnshnzut056f">
                <Link to={l.to} className="text-xs font-medium text-slate-400 transition-colors hover:text-orange" data-id="element-55" data-mp="1inny20ylmubz">
                  {labels[l.key]}
                </Link>
              </li>)}
          </ul>
          <p className="mt-4 text-center text-xs text-slate-500 leading-relaxed" data-id="element-56" data-mp="13xyih0prens6">
            © 2026 {company.website} · {company.legalName} · {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>;
}